import { ReactElement, Fragment, useState } from 'react';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Dialog from 'components/molecules/Dialog/Dialog';
import Select from 'components/molecules/Select/Select';
import { AppBaseButton } from 'components/AppBaseButton/AppBaseButton';
import { MaxLengthTextField } from 'components/FormFields/MaxLengthTextField';
import { NumericTextField } from 'components/FormFields/NumericTextField';
import { ZoneDetails, Zone } from 'types/domainTypes';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material';
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog';

const MAX_ZONES = 50;

type ZoneDialogFormData = {
  numberOfZones: number | '';
  zones: { name: string; spans: number | ''; spanLength: number | ''; suction: number | '' }[];
};

type ZonesDialogComponentProps = {
  zone?: Zone;
  newZone?: boolean;
  onClose: () => void;
  onSave: (data: ZoneDetails) => void;
};

const ZonesDialogComponent = ({ zone, newZone, onClose, onSave }: ZonesDialogComponentProps): ReactElement => {
  const { t } = useTranslation();
  const theme = useTheme();
  const discardDialogFullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const defaultZone: { name: string; spans: number | ''; spanLength: number | ''; suction: number | '' } = {
    name: '',
    spans: '',
    spanLength: '',
    suction: '',
  };
  const isAddOrEditMode = zone || newZone;
  const [isDiscardConfirmationOpen, setDiscardConfirmationOpen] = useState(false);
  const [numberOfZones, setNumberOfZones] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<number>(zone || newZone ? 1 : 0);
  const { control, getValues, setValue, handleSubmit, trigger } = useForm<ZoneDialogFormData>({
    mode: 'onChange',
    defaultValues: {
      numberOfZones: '',
      zones: zone ? [{ ...zone }] : newZone ? [{ ...defaultZone }] : [],
    },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'zones' });

  const finalStep = () => numberOfZones !== 0 && currentStep === numberOfZones;

  const removeZones = () => {
    const count = getValues('numberOfZones') as number;

    for (let i = numberOfZones; i > count; i--) {
      remove(i - 1);
    }

    setNumberOfZones(count);
  };

  const handleConfirmationDialogClose = () => {
    setValue('numberOfZones', numberOfZones);
    setDiscardConfirmationOpen(false);
  };

  const handleConfirmationDialogConfirm = () => {
    removeZones();
    setDiscardConfirmationOpen(false);
    setCurrentStep(currentStep + 1);
  };

  const handleBackOnClick = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNextOnClick = async () => {
    const isStepValid = await trigger();

    if (currentStep === 0 && isStepValid) {
      const count = getValues('numberOfZones') as number;

      if (numberOfZones === 0) {
        // If we have no zones configured then just append the number entered.
        for (let i = 0; i < count; i++) {
          append(defaultZone);
        }
      } else if (count > numberOfZones) {
        // If the number of zones entered is greater than the number we have
        // then append the extra zones.
        for (let i = 0; i < count - numberOfZones; i++) {
          append(defaultZone);
        }
      } else if (count < numberOfZones) {
        // If the number of zones entered is less than the number we have
        // then remove the extra zones.
        const checkZone = getValues('zones')[count];
        if (checkZone.name !== '' || checkZone.spanLength !== '' || checkZone.spans !== '' || checkZone.suction !== '') {
          setDiscardConfirmationOpen(true);
          return;
        } else {
          removeZones();
          setDiscardConfirmationOpen(false);
        }
      }

      if (numberOfZones !== count) {
        setNumberOfZones(count);
      }
    }

    if (isStepValid) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleOnSave = (formData: ZoneDetails) => {
    onSave({
      ...formData,
      zones: formData.zones.map((z) => {
        z.suction = -Math.abs(z.suction);
        return z;
      }),
    });
  };

  return (
    <>
      <Dialog
        title={newZone ? t('zones-dialog.title-add') : zone ? t('zones-dialog.title-edit') : t('zones-dialog.title')}
        open
        stretch
        onDialogClose={onClose}
      >
        <DialogContent dividers>
          {!isAddOrEditMode && currentStep === 0 && (
            <NumericTextField
              name="numberOfZones"
              control={control}
              label={t('zones-dialog.label-number-of-zones')}
              maxValue={{
                value: MAX_ZONES,
                errorText: t('zones-dialog.error-max-zones', { maxZones: MAX_ZONES }),
              }}
              required
              fullWidth
            />
          )}
          {currentStep > 0 &&
            fields.map((field, index) => (
              <Fragment key={field.id}>
                {currentStep === index + 1 && (
                  <Stack spacing={2}>
                    {!isAddOrEditMode && (
                      <Typography variant="h6" fontFamily="KingspanBrownTT-Bold">
                        {t('zones-dialog.subtitle', { zone: currentStep, zones: numberOfZones })}
                      </Typography>
                    )}
                    <MaxLengthTextField
                      name={`zones.${index}.name`}
                      data-testid={`zone-${index}-name`}
                      control={control}
                      required
                      label={t('zones-dialog.label-zone-name')}
                      fullWidth
                      maxLength={100}
                      multiline
                    />
                    <Controller
                      control={control}
                      name={`zones.${index}.spans`}
                      rules={{ required: true }}
                      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                        <Select
                          required
                          label={t('zones-dialog.label-number-of-spans')}
                          errorText={error?.type === 'required' ? t('common.required-error-text') : ''}
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value || ''}
                        >
                          {Array.from({ length: 11 }, (_, i) => i + 1).map((spanValue, spanIndex) => (
                            <MenuItem key={spanIndex} value={spanValue}>
                              {spanValue}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    <NumericTextField
                      name={`zones.${index}.spanLength`}
                      control={control}
                      label={t('zones-dialog.label-all-span-lengths')}
                      fullWidth
                      required
                      adornmentProps={{ endAdornment: 'm' }}
                      decimalPlaces={4}
                      validateOnBlur
                    />
                    <NumericTextField
                      name={`zones.${index}.suction`}
                      required
                      control={control}
                      label={t('zones-dialog.label-uniform-suction-load')}
                      fullWidth
                      adornmentProps={{ endAdornment: 'kN/m²' }}
                      decimalPlaces={3}
                      allowNegative
                      validateOnBlur
                    />
                  </Stack>
                )}
              </Fragment>
            ))}
        </DialogContent>
        <DialogActions
          sx={{
            padding: '16px 24px',
            justifyContent: !isAddOrEditMode && currentStep > 0 ? 'space-between' : undefined,
            alignItems: isAddOrEditMode || currentStep === 0 ? 'flex-end' : undefined,
          }}
        >
          {isAddOrEditMode && (
            <AppBaseButton variant="contained" onClick={handleSubmit(handleOnSave)}>
              {zone && t('zones-dialog.action-save-changes')}
              {newZone && t('zones-dialog.action-add-zone')}
            </AppBaseButton>
          )}
          {!isAddOrEditMode && (
            <>
              {currentStep > 0 && (
                <AppBaseButton variant="outlined" onClick={handleBackOnClick}>
                  {t('zones-dialog.action-back')}
                </AppBaseButton>
              )}
              <AppBaseButton variant="contained" onClick={finalStep() ? handleSubmit(handleOnSave) : handleNextOnClick}>
                {finalStep() ? t('zones-dialog.action-add-zones', { count: numberOfZones }) : t('zones-dialog.action-next')}
              </AppBaseButton>
            </>
          )}
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        isOpen={isDiscardConfirmationOpen}
        isFullscreen={discardDialogFullScreen}
        handleClose={handleConfirmationDialogClose}
        handleConfirm={handleConfirmationDialogConfirm}
        title={t('common.edit-dialog.discard-dialog.header')}
        questionText={t('zones-dialog.confirmation-dialog.question')}
        cancelText={t('common.edit-dialog.discard-dialog.action-cancel')}
        confirmText={t('common.edit-dialog.discard-dialog.action-confirm')}
      />
    </>
  );
};

export default ZonesDialogComponent;
